import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleRedirect() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleButton() {
  return (
    <button type="button" onClick={googleRedirect} data-testid="google-login-btn"
      className="w-full h-11 rounded-lg border border-slate-300 flex items-center justify-center gap-2 text-sm font-medium text-sp-navy hover:bg-slate-50 transition-colors">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
      Continue with Google
    </button>
  );
}

function OrDivider() {
  return <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="flex-1 h-px bg-slate-200" /></div>;
}

export function AuthCallback() {
  const { googleLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return; done.current = true;
    const m = loc.hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/login", { replace: true }); return; }
    googleLogin(decodeURIComponent(m[1]))
      .then((u) => { window.history.replaceState(null, "", "/app"); nav("/app", { replace: true }); })
      .catch(() => setError("Google sign-in failed. Please try again."));
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sp-navy text-white" data-testid="auth-callback">
      {error ? (<><p className="text-sm">{error}</p><Link to="/login" className="mt-3 text-sp-orange">Back to sign in</Link></>)
        : (<><div className="h-8 w-8 border-2 border-white/20 border-t-sp-orange rounded-full animate-spin" /><p className="mt-4 text-sm text-white/70">Signing you in…</p></>)}
    </div>
  );
}

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-sp-navy">
        <img src={IMG.siteSunset} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-sp-navy via-sp-navy/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/"><Logo dark /></Link>
          <div>
            <h2 className="text-4xl font-bold leading-tight">Plan. Build.<br />Manage. Grow.</h2>
            <p className="mt-4 text-white/60 max-w-sm">The operating system for construction, built for how India builds.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="text-2xl font-bold text-sp-navy">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      nav(u.role === "admin" ? "/app" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Sitepar workspace">
      <div className="mt-8"><GoogleButton /></div>
      <OrDivider />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><Label>Email</Label><Input data-testid="login-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" /></div>
        <div><Label>Password</Label><Input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" /></div>
        {error && <div data-testid="login-error" className="text-sm text-red-600">{error}</div>}
        <Button data-testid="login-submit" disabled={loading} className="w-full h-11 bg-sp-orange hover:bg-sp-orangedark rounded-lg">{loading ? "Signing in..." : "Sign In"}</Button>
      </form>
      <p className="mt-6 text-sm text-slate-500 text-center">No account? <Link to="/register" className="text-sp-orange font-medium">Start a project</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      nav("/verify-email");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start building with Sitepar in minutes">
      <div className="mt-8"><GoogleButton /></div>
      <OrDivider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div><Label>Full name</Label><Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" /></div>
        <div><Label>Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" /></div>
        <div><Label>Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1.5" /></div>
        {error && <div data-testid="register-error" className="text-sm text-red-600">{error}</div>}
        <Button data-testid="register-submit" disabled={loading} className="w-full h-11 bg-sp-orange hover:bg-sp-orangedark rounded-lg">{loading ? "Creating..." : "Create Account"}</Button>
      </form>
      <p className="mt-6 text-sm text-slate-500 text-center">Already have an account? <Link to="/login" className="text-sp-orange font-medium">Sign in</Link></p>
    </AuthShell>
  );
}
